import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Eye, EyeOff, CheckCircle, XCircle, MoreVertical, Shield, ShieldAlert, Ban, ChevronLeft, ChevronRight, StickyNote, CalendarPlus, ArrowUpDown, Loader } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVendors = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adminStatusFilter, setAdminStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);

  // Detail dialog
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Notes dialog
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesProvider, setNotesProvider] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  // Extend trial dialog
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendProvider, setExtendProvider] = useState(null);
  const [extendDays, setExtendDays] = useState(14);
  const [extendSaving, setExtendSaving] = useState(false);

  // Change plan dialog
  const [planOpen, setPlanOpen] = useState(false);
  const [planProvider, setPlanProvider] = useState(null);
  const [newPlan, setNewPlan] = useState('basic');
  const [planSaving, setPlanSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (adminStatusFilter !== 'all') params.append('admin_status', adminStatusFilter);
      params.append('page', page);
      params.append('per_page', perPage);

      const response = await axios.get(`${API}/admin/providers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProviders(response.data.providers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading providers:', error);
      toast.error('Fehler beim Laden der Dienstleister');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, adminStatusFilter, page, perPage]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadProviders();
    loadStats();
  }, [loadProviders]);

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setPage(1);
    }, 300));
  };

  const toggleVisibility = async (providerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${providerId}/toggle-visibility`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sichtbarkeit geändert');
      loadProviders();
    } catch (error) {
      toast.error('Fehler beim Ändern');
    }
  };

  const setSubscriptionStatus = async (providerId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${providerId}/set-subscription?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abo-Status aktualisiert');
      loadProviders();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const setAdminStatus = async (providerId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${providerId}/admin-status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Freigabestatus aktualisiert');
      loadProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleExtendTrial = async () => {
    if (!extendProvider) return;
    try {
      setExtendSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${extendProvider.id}/extend-trial`,
        { days: parseInt(extendDays) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Testphase um ${extendDays} Tage verlängert`);
      setExtendOpen(false);
      loadProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verlängern');
    } finally {
      setExtendSaving(false);
    }
  };

  const handleChangePlan = async () => {
    if (!planProvider) return;
    try {
      setPlanSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${planProvider.id}/set-plan`,
        { plan_level: newPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan auf ${newPlan.charAt(0).toUpperCase() + newPlan.slice(1)} geändert`);
      setPlanOpen(false);
      loadProviders();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern');
    } finally {
      setPlanSaving(false);
    }
  };

  const openDetail = async (providerId) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/provider/${providerId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProvider(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openNotes = (provider) => {
    setNotesProvider(provider);
    setNotesText(provider.profile?.admin_notes || '');
    setNotesOpen(true);
  };

  const saveNotes = async () => {
    if (!notesProvider) return;
    try {
      setNotesSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/provider/${notesProvider.profile.id}/admin-notes`,
        { notes: notesText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notizen gespeichert');
      setNotesOpen(false);
      loadProviders();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setNotesSaving(false);
    }
  };

  const getAdminStatusBadge = (status) => {
    const badges = {
      approved: { text: 'Genehmigt', color: 'bg-green-100 text-green-800' },
      pending: { text: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' },
      flagged: { text: 'Markiert', color: 'bg-orange-100 text-orange-800' },
      blocked: { text: 'Blockiert', color: 'bg-red-100 text-red-800' },
    };
    const badge = badges[status] || badges.approved;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const getSubscriptionBadge = (status) => {
    const badges = {
      trial: { text: 'Testphase', color: 'bg-yellow-100 text-yellow-800' },
      active: { text: 'Aktiv', color: 'bg-green-100 text-green-800' },
      expired: { text: 'Abgelaufen', color: 'bg-red-100 text-red-800' },
      none: { text: 'Kein Abo', color: 'bg-gray-100 text-gray-800' },
    };
    const badge = badges[status] || badges.none;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const badges = {
      basic: { text: 'Basic', color: 'bg-[#FFF0D1] text-[#2B2142]' },
      plus: { text: 'Plus', color: 'bg-indigo-100 text-indigo-800' },
      pro: { text: 'Pro', color: 'bg-purple-100 text-purple-800' },
    };
    const badge = badges[plan] || badges.basic;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6">
      {/* Plan Distribution Summary */}
      {stats && stats.plan_distribution && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">Aktive Abos nach Plan</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-3xl font-bold text-[#2B2142]">{stats.plan_distribution?.basic || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Basic</p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-3xl font-bold text-indigo-600">{stats.plan_distribution?.plus || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Plus</p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-3xl font-bold text-purple-600">{stats.plan_distribution?.pro || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Pro</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suche nach Name oder E-Mail..."
            value={search}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B2142]"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Abo-Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="active">Aktiv</SelectItem>
            <SelectItem value="trial">Testphase</SelectItem>
            <SelectItem value="expired">Abgelaufen</SelectItem>
          </SelectContent>
        </Select>
        <Select value={adminStatusFilter} onValueChange={(v) => { setAdminStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Freigabe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="approved">Genehmigt</SelectItem>
            <SelectItem value="pending">Ausstehend</SelectItem>
            <SelectItem value="flagged">Markiert</SelectItem>
            <SelectItem value="blocked">Blockiert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-600">
        {total} Dienstleister gefunden
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-[#2B2142]" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Firmenname</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">E-Mail</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Kategorien</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Standort</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Sichtbar</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Plan</th>
                    {/* <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Laufzeit bis</th> */}
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Freigabe</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {providers.map(({ profile, user: providerUser }) => (
                    <tr key={profile.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{profile.company_name || 'Kein Name'}</span>
                          {profile.is_complete ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-yellow-600" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{providerUser?.email}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {profile.categories?.slice(0, 2).map((cat) => (
                            <span key={cat} className="px-2 py-0.5 bg-[#FFF0D1] text-[#2B2142] rounded text-xs">
                              {cat}
                            </span>
                          ))}
                          {profile.categories?.length > 2 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              +{profile.categories.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {profile.location_city || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleVisibility(profile.id)}
                          className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold ${
                            profile.is_visible
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {profile.is_visible ? (
                            <><Eye className="w-3 h-3" /> Sichtbar</>
                          ) : (
                            <><EyeOff className="w-3 h-3" /> Versteckt</>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {getPlanBadge(profile.plan_level)}
                      </td>
                      {/* <td className="px-6 py-4 text-sm text-gray-600">
                        {profile.subscription_end
                          ? new Date(profile.subscription_end).toLocaleDateString('de-DE')
                          : profile.trial_end
                            ? new Date(profile.trial_end).toLocaleDateString('de-DE')
                            : '-'}
                      </td> */}
                      <td className="px-6 py-4">
                        {getAdminStatusBadge(profile.admin_status || 'approved')}
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="p-2 hover:bg-gray-100 rounded-lg">
                            <MoreVertical className="w-4 h-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(profile.id)}>
                              <Eye className="w-4 h-4 mr-2" /> Details anzeigen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openNotes({ profile, user: providerUser })}>
                              <StickyNote className="w-4 h-4 mr-2" /> Notizen bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setExtendProvider(profile); setExtendDays(14); setExtendOpen(true); }}>
                              <CalendarPlus className="w-4 h-4 mr-2" /> Testphase verlängern
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setPlanProvider(profile); setNewPlan(profile.plan_level || 'basic'); setPlanOpen(true); }}>
                              <ArrowUpDown className="w-4 h-4 mr-2" /> Plan ändern
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setAdminStatus(profile.id, 'approved')}>
                              <Shield className="w-4 h-4 mr-2" /> Genehmigen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setAdminStatus(profile.id, 'flagged')}>
                              <ShieldAlert className="w-4 h-4 mr-2" /> Markieren
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setAdminStatus(profile.id, 'blocked')} className="text-red-600">
                              <Ban className="w-4 h-4 mr-2" /> Blockieren
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {providers.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-600">Keine Dienstleister gefunden</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Seite {page} von {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dienstleister Details</DialogTitle>
            <DialogDescription>Detaillierte Informationen zum Dienstleister-Profil.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8"><Loader className="w-8 h-8 animate-spin text-[#2B2142]" /></div>
          ) : selectedProvider ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Firmenname</p>
                  <p className="font-semibold">{selectedProvider.profile?.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-Mail</p>
                  <p className="font-semibold">{selectedProvider.user?.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Kontaktperson</p>
                  <p className="font-semibold">{selectedProvider.profile?.contact_person || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Standort</p>
                  <p className="font-semibold">{selectedProvider.profile?.location_city || '-'}, {selectedProvider.profile?.location_state || ''}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Plan</p>
                  <p className="font-semibold capitalize">{selectedProvider.profile?.plan_level || 'basic'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Abo-Status</p>
                  {getSubscriptionBadge(selectedProvider.profile?.subscription_status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Freigabe</p>
                  {getAdminStatusBadge(selectedProvider.profile?.admin_status || 'approved')}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sichtbar</p>
                  <p className="font-semibold">{selectedProvider.profile?.is_visible ? 'Ja' : 'Nein'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Statistiken</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-lg font-bold">{selectedProvider.view_count || 0}</p>
                    <p className="text-xs text-gray-500">Profilaufrufe</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-lg font-bold">{selectedProvider.request_count || 0}</p>
                    <p className="text-xs text-gray-500">Anfragen</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-lg font-bold">{selectedProvider.review_count || 0}</p>
                    <p className="text-xs text-gray-500">Bewertungen</p>
                  </div>
                </div>
              </div>

              {selectedProvider.profile?.admin_notes && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Admin-Notizen</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProvider.profile.admin_notes}</p>
                </div>
              )}

              {selectedProvider.subscription && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Abo-Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Stripe Abo-ID</p>
                      <p className="font-mono text-xs">{selectedProvider.subscription.stripe_subscription_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Laufzeit bis</p>
                      <p>{selectedProvider.subscription.current_period_end ? new Date(selectedProvider.subscription.current_period_end).toLocaleDateString('de-DE') : '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4 text-sm text-gray-500">
                <p>Erstellt: {selectedProvider.user?.created_at ? new Date(selectedProvider.user.created_at).toLocaleDateString('de-DE') : '-'}</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin-Notizen: {notesProvider?.profile?.company_name || ''}</DialogTitle>
            <DialogDescription>Interne Notizen zu diesem Dienstleister bearbeiten.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              className="w-full h-40 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B2142] resize-none"
              placeholder="Interne Notizen zu diesem Dienstleister..."
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setNotesOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              onClick={saveNotes}
              disabled={notesSaving}
              className="px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
            >
              {notesSaving ? 'Speichert...' : 'Speichern'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Testphase verlängern: {extendProvider?.company_name || ''}</DialogTitle>
            <DialogDescription>Testphase um eine bestimmte Anzahl an Tagen verlängern.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">Anzahl Tage</label>
            <input
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              min="1"
              max="365"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B2142]"
            />
            <p className="text-xs text-gray-500 mt-2">Die Testphase wird um die angegebene Anzahl an Tagen verlängert.</p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setExtendOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              onClick={handleExtendTrial}
              disabled={extendSaving}
              className="px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
            >
              {extendSaving ? 'Verlängert...' : 'Verlängern'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Plan ändern: {planProvider?.company_name || ''}</DialogTitle>
            <DialogDescription>Abonnement-Plan für diesen Dienstleister ändern.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">Neuer Plan</label>
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="plus">Plus</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-2">
              Aktueller Plan: <strong className="capitalize">{planProvider?.plan_level || 'basic'}</strong>
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setPlanOpen(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Abbrechen
            </button>
            <button
              onClick={handleChangePlan}
              disabled={planSaving}
              className="px-4 py-2 bg-[#2B2142] text-white rounded-lg hover:bg-[#1E1730] disabled:opacity-50"
            >
              {planSaving ? 'Ändert...' : 'Plan ändern'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVendors;
